import type { AnaestheticCaseInput, LocalAnaestheticCase } from '@iculogbook/shared';
import { uuidv7 } from 'uuidv7';

import { db } from '../db.ts';
import { ensurePersistentStorage } from '../persist.ts';
import {
  byOccurredAtDescending,
  isLive,
  notifyLocalWrite,
  outboxItem,
  userRange,
  writeWithOutbox,
} from './internal.ts';

/**
 * Anaesthetic cases. Same terms as every other repository: components never
 * touch Dexie, a write and its outbox item are one transaction, and nothing is
 * ever hard deleted.
 *
 * **Import-only.** There is no `create` for a single case and no `duplicate`,
 * because there is no form that makes one — the RCoA platform is the system of
 * record for anaesthetics and this app imports from it. `update`, `softDelete`
 * and `restore` all exist, because a trainee must be able to correct or remove
 * something an import got wrong.
 */

export const anaestheticRepository = {
  /**
   * Writes an imported batch in one transaction, each case queued for sync.
   *
   * Every case carries the same `importBatchId`, which is what makes "undo this
   * import" possible: a trainee who imports 1,296 anaesthetic cases and then
   * thinks better of it needs one button, not 1,296 deletions.
   */
  async importBatch(
    userId: string,
    inputs: readonly AnaestheticCaseInput[],
    importBatchId: string,
  ): Promise<number> {
    if (inputs.length === 0) return 0;

    const now = new Date();

    const records: LocalAnaestheticCase[] = inputs.map((input) => ({
      ...input,
      id: uuidv7(),
      userId,
      source: 'import',
      importBatchId,
      createdAt: now,
      updatedAt: now,
      syncedAt: null,
    }));

    await db.transaction('rw', db.anaestheticCases, db.outbox, async () => {
      for (const record of records) {
        await db.anaestheticCases.add(record);
        await db.outbox.add(outboxItem('anaesthetic', record.id, userId));
      }
    });

    notifyLocalWrite();
    void ensurePersistentStorage();

    return records.length;
  },

  /**
   * Undoes an import. Soft delete, like every other removal here — the rows
   * stay and sync as deletions, so undoing on one device undoes it everywhere.
   */
  async undoImport(userId: string, importBatchId: string): Promise<number> {
    const all = await db.anaestheticCases.toArray();
    const batch = all.filter(
      (record) =>
        record.userId === userId &&
        record.importBatchId === importBatchId &&
        record.deletedAt === undefined,
    );

    if (batch.length === 0) return 0;

    const now = new Date();

    await db.transaction('rw', db.anaestheticCases, db.outbox, async () => {
      for (const record of batch) {
        await db.anaestheticCases.update(record.id, {
          deletedAt: now,
          updatedAt: now,
          syncedAt: null,
        });
        await db.outbox.add(outboxItem('anaesthetic', record.id, userId));
      }
    });

    notifyLocalWrite();
    return batch.length;
  },

  /** The import batches on this device, newest first, for the undo list. */
  async importBatches(
    userId: string,
  ): Promise<{ importBatchId: string; count: number; importedAt: Date }[]> {
    const all = await db.anaestheticCases.toArray();
    const batches = new Map<string, { count: number; importedAt: Date }>();

    for (const record of all) {
      const batchId = record.importBatchId;
      if (record.userId !== userId || batchId === undefined || record.deletedAt !== undefined) {
        continue;
      }

      const existing = batches.get(batchId);
      if (existing === undefined) batches.set(batchId, { count: 1, importedAt: record.createdAt });
      else existing.count += 1;
    }

    return [...batches.entries()]
      .map(([importBatchId, value]) => ({ importBatchId, ...value }))
      .sort((a, b) => b.importedAt.getTime() - a.importedAt.getTime());
  },

  async update(
    userId: string,
    id: string,
    changes: Partial<AnaestheticCaseInput>,
  ): Promise<void> {
    const existing = await db.anaestheticCases.get(id);
    if (existing === undefined || !isLive(existing, userId)) return;

    await writeWithOutbox('anaesthetic', id, userId, async () => {
      await db.anaestheticCases.update(id, {
        ...changes,
        updatedAt: new Date(),
        syncedAt: null,
      });
    });
  },

  /** Soft delete only. Nothing here removes a row. */
  async softDelete(userId: string, id: string): Promise<void> {
    const existing = await db.anaestheticCases.get(id);
    if (existing === undefined || !isLive(existing, userId)) return;

    const now = new Date();

    await writeWithOutbox('anaesthetic', id, userId, async () => {
      await db.anaestheticCases.update(id, { deletedAt: now, updatedAt: now, syncedAt: null });
    });
  },

  async restore(userId: string, id: string): Promise<void> {
    const existing = await db.anaestheticCases.get(id);
    if (existing === undefined || existing.userId !== userId) return;

    await writeWithOutbox('anaesthetic', id, userId, async () => {
      await db.anaestheticCases.update(id, {
        deletedAt: undefined,
        updatedAt: new Date(),
        syncedAt: null,
      });
    });
  },

  /** Writes cases back from an export, ids preserved so a restore is idempotent. */
  async restoreFromExport(
    userId: string,
    records: readonly LocalAnaestheticCase[],
  ): Promise<number> {
    if (records.length === 0) return 0;

    await db.transaction('rw', db.anaestheticCases, db.outbox, async () => {
      for (const record of records) {
        await db.anaestheticCases.put({
          ...record,
          userId,
          source: 'restore',
          updatedAt: record.updatedAt,
          syncedAt: null,
        });
        await db.outbox.add(outboxItem('anaesthetic', record.id, userId));
      }
    });

    notifyLocalWrite();
    void ensurePersistentStorage();

    return records.length;
  },

  async get(userId: string, id: string): Promise<LocalAnaestheticCase | undefined> {
    const record = await db.anaestheticCases.get(id);
    return record !== undefined && isLive(record, userId) ? record : undefined;
  },

  /** Newest first. Excludes deleted rows and anything belonging to another user. */
  async list(userId: string): Promise<LocalAnaestheticCase[]> {
    const [lower, upper] = userRange(userId);
    const records = await db.anaestheticCases
      .where('[userId+occurredAt]')
      .between(lower, upper, true, true)
      .toArray();

    return records.filter((record) => isLive(record, userId)).sort(byOccurredAtDescending);
  },

  async listDeleted(userId: string): Promise<LocalAnaestheticCase[]> {
    const records = await db.anaestheticCases.toArray();

    return records
      .filter((record) => record.userId === userId && record.deletedAt !== undefined)
      .sort(byOccurredAtDescending);
  },
};
